import { AllowedLanguages, currentLanguage } from 'i18n';

import { LocalStorageService } from 'services/localStorage/localStorage';
import { logError } from 'services/logging/logging';
import { GenericError } from 'support/helpers/errors/errors';

const LOCAL_STORAGE_KEY = 'dispute-email-language-by-partner';

const ALLOWED_LANGUAGES: Array<AllowedLanguages> = ['en', 'de'];

/**
 * The language a dispute email to this partner should be written in. Partners are remembered
 * individually because the choice belongs to who reads the email, not to who writes it: one buyer
 * commonly disputes with German and English speaking suppliers from the same account.
 *
 * Falls back to the account language, which is what everyone gets until they pick something for a
 * given partner.
 */
export const getEmailLanguageForPartner = (partnerId: string | undefined): AllowedLanguages => {
  if (!partnerId) {
    return currentLanguage;
  }
  const stored = readMap()[partnerId];
  return isAllowedLanguage(stored) ? stored : currentLanguage;
};

export const setEmailLanguageForPartner = (partnerId: string | undefined, language: AllowedLanguages): void => {
  if (!partnerId) {
    return;
  }
  LocalStorageService.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ ...readMap(), [partnerId]: language }), (error) =>
    report('Failed to store the dispute email language', { partnerId, language, originalError: error }),
  );
};

const isAllowedLanguage = (value: unknown): value is AllowedLanguages =>
  ALLOWED_LANGUAGES.includes(value as AllowedLanguages);

const report = (message: string, context: Record<string, unknown>) =>
  logError(new GenericError(message, { key: LOCAL_STORAGE_KEY, ...context }));

const readMap = (): Record<string, unknown> => {
  const rawValue = LocalStorageService.getItem(LOCAL_STORAGE_KEY, (error) =>
    report('Failed to read the dispute email language', { originalError: error }),
  );
  if (!rawValue) {
    return {};
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawValue);
  } catch (error) {
    report('Failed to parse the dispute email language', { rawValue, originalError: error });
    return {};
  }

  if (typeof parsed !== 'object' || parsed === null) {
    report('The dispute email language entry is not an object', { rawValue });
    return {};
  }

  return parsed as Record<string, unknown>;
};
